import { Component, OnInit, ViewChild, Output, EventEmitter } from '@angular/core';
import { OrganisationSettingsService } from '../../app/fsl/services/organisation-settings.service';
import { ContextDiscoveryEventBusService } from '../../context-discovery-services/services';
import { Message } from '../../context-discovery-services/models';
import { SeverityType } from '../../context-discovery-services/constants/severity-type.const';
import { GatewayTenantService } from '../../app/fsl/services/gateway-tenant/gateway-tenant.service';
import { LaunchDarklyService } from '../../context-discovery-services/services/launch-darkly.service';
import { FeatureFlags } from '../../context-discovery-services/constants/feature-flags.const';
import { SiteType } from '../../context-discovery-services/enums/site-type';

@Component({
  selector: 'adminapp-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss']
})
export class LayoutComponent implements OnInit {

  existingSiteType: String;
  organizationSettings: any;
  selectedSiteType: string;
  isSiteTypeUpdationInProgress = false;
  isTenantAccountCreationGatewayEnabled: boolean;

  @ViewChild('saveSiteType') saveSiteTypeComponent;
  @ViewChild('selectedSiteType') selectedSiteTypeComponent;

  @Output()
  siteSelectedEventEmitter = new EventEmitter<boolean>();

  constructor(private organisationSettingsService: OrganisationSettingsService,
    private eventBusService: ContextDiscoveryEventBusService,
    private tenantService: GatewayTenantService,
    private ldService: LaunchDarklyService) {
    this.eventBusService.serverMessage$.subscribe((message: Message) => {
      const summaryToast: any = document.getElementById(message.severityType + 'SiteTypeSelectionToast');
      summaryToast.text = message.content;
      summaryToast.open();
    });

    this.isTenantAccountCreationGatewayEnabled = this.ldService.getBooleanFeatureFlagValue(FeatureFlags.GATEWAY_TENANT);
    this.ldService.flagChange.subscribe(() => {
      this.isTenantAccountCreationGatewayEnabled = this.ldService.getBooleanFeatureFlagValue(FeatureFlags.GATEWAY_TENANT, true);
    });
  }

  ngOnInit() {
    this.organisationSettingsService.getSettings().subscribe(
      this.getOrganizationSettingsSuccessCallback.bind(this),
      this.getOrganizationSettingsFailureCallback.bind(this));
  }

  getOrganizationSettingsSuccessCallback(response) {
    this.organizationSettings = response;
    if (response.General) {
      this.existingSiteType = response.General.SiteType;
    }
  }

  getOrganizationSettingsFailureCallback() {
    this.handleFailure('Something went wrong. Please try again.');
  }

  openSiteTypeSaveModal() {
    if (this.existingSiteType) {
      this.openSaveSiteTypeComponent();
    } else {
      this.organizationSettings.General.SiteType = this.selectedSiteType;
      this.isSiteTypeUpdationInProgress = true;
      if (this.organizationSettings.General.SiteType.toUpperCase() === SiteType.TYPE_GATEWAY
          && this.isTenantAccountCreationGatewayEnabled) {
        this.createCustomerTenantAccount();
      } else {
        this.saveSiteTypeSettings();
      }
    }
  }

  openSaveSiteTypeComponent() {
    this.saveSiteTypeComponent.openSaveDialog();
  }

  handleSiteSelectedEvent() {
    if (this.organizationSettings && this.selectedSiteType) {
      this.isSiteTypeUpdationInProgress = true;
      this.organizationSettings.General.SiteType = this.selectedSiteType;
      if (this.selectedSiteType.toUpperCase() === SiteType.TYPE_GATEWAY && this.isTenantAccountCreationGatewayEnabled) {
        this.createCustomerTenantAccount();
      } else {
        this.saveSiteTypeSettings();
      }
    } else {
      this.showErrorOperationToast('Failed to ' + (!!this.existingSiteType ? 'change' : 'set') + ' site type. Please try again.');
    }
  }

  createCustomerTenantAccount() {
    this.tenantService.createGatewayTenantAccount()
      .subscribe(this.createTenantAccountSuccessCallback.bind(this),
        this.createTenantAccountFailureCallback.bind(this));
  }

  createTenantAccountSuccessCallback() {
    this.saveSiteTypeSettings();
  }

  createTenantAccountFailureCallback() {
      this.isSiteTypeUpdationInProgress = false;
      const message = 'Unable to ' + (!!this.existingSiteType ? 'change' : 'set') + ' site type.';
      this.showErrorOperationToast(message + '\n' + 'Failed to create Gateway Account. Please try again.');
  }

  saveSiteTypeSettings() {
    this.organisationSettingsService.setSettings(this.organizationSettings)
      .subscribe(this.saveSiteTypeSuccessCallback.bind(this),
        this.saveSiteTypeFailureCallback.bind(this));
  }

  saveSiteTypeSuccessCallback() {
    this.isSiteTypeUpdationInProgress = false;
    this.showSuccessOperationToast('Successfully ' + (!!this.existingSiteType ? 'changed' : 'set') + ' site type.');
    this.postSuccessMessageToParent();
    this.existingSiteType = this.organizationSettings.General.SiteType;
  }

  postSuccessMessageToParent() {
    parent.postMessage({ eventId: 'SiteTypeChanged', siteType: this.selectedSiteType }, '*');
  }

  saveSiteTypeFailureCallback() {
    this.isSiteTypeUpdationInProgress = false;
    this.showErrorOperationToast('Failed to ' + (!!this.existingSiteType ? 'change' : 'set') + ' site type. Please try again.');
  }

  handleFailure(message: string) {
    this.eventBusService.sendMessage(new Message(message, SeverityType.CRITICAL));
  }

  showSuccessOperationToast(message: string) {
    this.eventBusService.sendMessage(new Message(message, SeverityType.SUCCESS));
  }

  showErrorOperationToast(message: string) {
    this.eventBusService.sendMessage(new Message(message, SeverityType.CRITICAL));
  }

  handleSelectedSiteEvent($event) {
    this.selectedSiteType = $event.siteType;
    this.siteSelectedEventEmitter.emit(this.selectedSiteType !== this.existingSiteType);
  }
}
